/**
 * F7 (pack & label) command payloads, as sent in `commands[].payload` of POST /sync/commands.
 * Sacks into the pack run, the leftover and spillage sacks and the DELABEL run reuse the F6
 * commands (weigh_in, record_output, close_run) — see features/process/commands.ts.
 *
 * Units: weights in INTEGER GRAMS (`_g`), as everywhere on the phone. Spot pouch weights are
 * whole grams as typed from the bench scale.
 */
import { z } from 'zod'
import { registerCommand } from '@/offline'
import '@/features/process/commands'

const id = z.string().min(1)
const isoTs = z.string().min(1)
const grams = z.number().int()
const count = z.number().int().nonnegative()

export const verifyPouchPayload = z.object({
  run_id: id,
  pack_lot_id: id,
  position: z.enum(['FIRST', 'LAST']),
  /** The decoded QR text, exactly as read. */
  scanned_text: z.string().min(1),
  /** The phone's comparison with the lot's QR link (the server compares again). */
  match: z.boolean(),
  /** The pouch photo; V1 checks the QR alone (no photo). */
  photo_id: id.nullable(),
})
export type VerifyPouchPayload = z.infer<typeof verifyPouchPayload>

export const weighPouchesPayload = z.object({
  run_id: id,
  pack_lot_id: id,
  weights: z
    .array(
      z.object({
        value_g: grams.positive(),
        occurred_at: isoTs,
        /** The pouch number in the run's count when it was asked for. */
        position: z.number().int().positive(),
      }),
    )
    .min(1),
  method: z.literal('MANUAL_ENTRY'),
  equipment_id: id.nullable(),
})
export type WeighPouchesPayload = z.infer<typeof weighPouchesPayload>

export const packClosePayload = z.object({
  run_id: id,
  pack_lot_id: id,
  pouches_good: count,
  pouches_rejected: count,
  labels_printed: count,
  labels_spoiled: count,
  labels_destroyed: count,
  last_pouch_label_ok: z.boolean(),
  /** The counter photo; V1 types the reading only (no photo). */
  counter_photo_id: id.nullable(),
  /** V1: the coder counter as read at the close (labels_printed = this − the line's last close reading). */
  counter_reading: count.optional(),
  /** V1: the line's reading at its previous close as this phone knows it (the server takes the higher of this and its own). */
  counter_previous: count.optional(),
  /** The phone's reconciliation and weight figures, for comparison only. */
  phone_totals: z.object({
    input_g: grams.nonnegative(),
    leftover_g: grams.nonnegative(),
    spillage_g: grams.nonnegative(),
    reconciliation_gap: z.number().int(),
  }),
})
export type PackClosePayload = z.infer<typeof packClosePayload>

registerCommand('verify_pouch', verifyPouchPayload)
registerCommand('weigh_pouches', weighPouchesPayload)
registerCommand('pack_close', packClosePayload)

export const PACK_COMMAND_TYPES = ['verify_pouch', 'weigh_pouches', 'pack_close'] as const
